// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - SUPERVISION IoT
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy / Joshua - Janvier 2026
//
// Page dédiée à la supervision des équipements IoT avec :
// - Vue synoptique de tous les modules
// - Contrôles ON/OFF/AUTO
// - Chaînage automatique visible
// - Données temps réel
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    Activity, Play, Pause, RefreshCw, Power, Settings,
    Thermometer, Droplets, Wind, Sun, Snowflake,
    AlertTriangle, CheckCircle, Clock, Zap
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Configuration types équipements
const TYPES_EQUIPEMENTS = {
    vanne: { icon: '💧', label: 'Vanne', color: '#3b82f6', colorClass: 'bg-blue-500' },
    pompe: { icon: '⚡', label: 'Pompe', color: '#f59e0b', colorClass: 'bg-amber-500' },
    brumisation: { icon: '🌫️', label: 'Brumisation', color: '#06b6d4', colorClass: 'bg-cyan-500' },
    ventilation: { icon: '💨', label: 'Ventilation', color: '#6b7280', colorClass: 'bg-gray-500' },
    chauffage: { icon: '🔥', label: 'Chauffage', color: '#ef4444', colorClass: 'bg-red-500' },
    refroidissement: { icon: '❄️', label: 'Refroidissement', color: '#0ea5e9', colorClass: 'bg-sky-500' },
    eclairage: { icon: '💡', label: 'Éclairage', color: '#eab308', colorClass: 'bg-yellow-500' },
    brasseur: { icon: '🌀', label: 'Brasseur', color: '#22c55e', colorClass: 'bg-green-500' },
    voile: { icon: '🛡️', label: 'Voile', color: '#a855f7', colorClass: 'bg-purple-500' },
    goutte_a_goutte: { icon: '🚿', label: 'Goutte-à-goutte', color: '#0ea5e9', colorClass: 'bg-sky-500' }
};

// Types de zones
const TYPES_ZONES = {
    vegetation: { icon: '🌱', colorClass: 'border-green-400 bg-green-50' },
    stockage: { icon: '❄️', colorClass: 'border-cyan-400 bg-cyan-50' },
    technique: { icon: '⚙️', colorClass: 'border-amber-400 bg-amber-50' },
    meteo: { icon: '🌤️', colorClass: 'border-purple-400 bg-purple-50' }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════
const SupervisionIoT = () => {
    // États
    const [simulationStatus, setSimulationStatus] = useState({ running: false, tickCount: 0 });
    const [etats, setEtats] = useState([]);
    const [equipements, setEquipements] = useState([]);
    const [equipementsActifs, setEquipementsActifs] = useState({});
    const [liensEau, setLiensEau] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [actionLog, setActionLog] = useState([]);

    // ═══════════════════════════════════════════════════════════════════════
    // CHARGEMENT DONNÉES
    // ═══════════════════════════════════════════════════════════════════════
    const loadData = useCallback(async () => {
        try {
            // Status simulation
            const statusRes = await fetch(`${API_BASE}/simulation/status`);
            const statusData = await statusRes.json();
            if (statusData.success) setSimulationStatus(statusData.data);

            // États simulés
            const etatsRes = await fetch(`${API_BASE}/simulation/etats`);
            const etatsData = await etatsRes.json();
            if (etatsData.success) setEtats(etatsData.data || []);

            // Équipements
            const eqRes = await fetch(`${API_BASE}/simulation/equipements`);
            const eqData = await eqRes.json();
            if (eqData.success) setEquipements(eqData.data || []);

            // Équipements actifs
            const eqActifsRes = await fetch(`${API_BASE}/simulation/equipements/actifs`);
            const eqActifsData = await eqActifsRes.json();
            if (eqActifsData.success) setEquipementsActifs(eqActifsData.byModule || {});

            // Liens eau
            const liensRes = await fetch(`${API_BASE}/simulation/liens-eau`);
            const liensData = await liensRes.json();
            if (liensData.success) setLiensEau(liensData.data || []);

            setLastUpdate(new Date());
            setLoading(false);
        } catch (err) {
            console.error('Erreur chargement:', err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 3000);
        return () => clearInterval(interval);
    }, [loadData]);

    // ═══════════════════════════════════════════════════════════════════════
    // ACTIONS
    // ═══════════════════════════════════════════════════════════════════════
    const toggleSimulation = async () => {
        const endpoint = simulationStatus.running ? 'stop' : 'start';
        await fetch(`${API_BASE}/simulation/${endpoint}`, { method: 'POST' });
        await loadData();
        addLog(`Simulation ${simulationStatus.running ? 'arrêtée' : 'démarrée'}`);
    };

    const resetSimulation = async () => {
        if (!window.confirm('Réinitialiser toute la simulation ?')) return;
        await fetch(`${API_BASE}/simulation/reset`, { method: 'POST' });
        await loadData();
        addLog('Simulation réinitialisée');
    };

    const toggleEquipement = async (equipement, newEtat) => {
        try {
            const res = await fetch(`${API_BASE}/simulation/equipements/${equipement.id}/etat`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ etat: newEtat, source: 'supervision' })
            });
            const data = await res.json();
            if (data.success) {
                await loadData();
                addLog(`${equipement.nom}: ${data.ancien_etat} → ${newEtat}`);
                
                // Log chaînage si applicable
                if (newEtat === 'on' && equipement.source_module_id) {
                    addLog(`⚡ Chaînage: Pompe activée automatiquement`);
                }
            }
        } catch (err) {
            console.error('Erreur toggle:', err);
        }
    };

    const addLog = (message) => {
        const now = new Date().toLocaleTimeString('fr-FR');
        setActionLog(prev => [{ time: now, message }, ...prev].slice(0, 20));
    };

    // ═══════════════════════════════════════════════════════════════════════
    // HELPERS
    // ═══════════════════════════════════════════════════════════════════════
    const getEquipementsByModule = (moduleId) => {
        return equipements.filter(e => e.module_id === moduleId);
    };

    const getEtatModule = (moduleId) => {
        return etats.find(e => e.module_id === moduleId);
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU CARTE MODULE
    // ═══════════════════════════════════════════════════════════════════════
    const renderModuleCard = (etat) => {
        const moduleEquipements = getEquipementsByModule(etat.module_id);
        const actifs = equipementsActifs[etat.module_id] || [];
        const typeZone = TYPES_ZONES[etat.type_zone] || TYPES_ZONES.vegetation;
        const isSelected = selectedModule === etat.module_id;

        return (
            <div
                key={etat.module_id}
                onClick={() => setSelectedModule(isSelected ? null : etat.module_id)}
                className={`
                    border-2 rounded-xl p-3 cursor-pointer transition-all
                    ${typeZone.colorClass}
                    ${isSelected ? 'ring-4 ring-blue-400 shadow-lg' : 'hover:shadow-md'}
                    ${actifs.length > 0 ? 'ring-2 ring-green-400' : ''}
                `}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <span className="text-xl">{etat.zone_icone || typeZone.icon}</span>
                        <div>
                            <h3 className="font-bold text-gray-800 text-sm">{etat.zone_nom || etat.module_nom}</h3>
                            <p className="text-xs text-gray-500">{etat.code_module}</p>
                        </div>
                    </div>
                    {actifs.length > 0 && (
                        <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
                            {actifs.length} ON
                        </span>
                    )}
                </div>

                {/* Mesures */}
                <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
                    {etat.temperature !== null && (
                        <div className="flex items-center gap-1 bg-white/50 rounded px-2 py-1">
                            <Thermometer className="w-3 h-3 text-red-500" />
                            <span className={etat.temperature < 10 ? 'text-blue-600 font-bold' : ''}>{etat.temperature?.toFixed(1)}°C</span>
                        </div>
                    )}
                    {etat.humidity !== null && (
                        <div className="flex items-center gap-1 bg-white/50 rounded px-2 py-1">
                            <Droplets className="w-3 h-3 text-blue-500" />
                            {etat.humidity?.toFixed(0)}%
                        </div>
                    )}
                    {etat.humidity_sol !== null && (
                        <div className="flex items-center gap-1 bg-white/50 rounded px-2 py-1">
                            <span className="text-xs">💧</span>
                            Sol: {etat.humidity_sol?.toFixed(0)}%
                        </div>
                    )}
                    {etat.co2 !== null && etat.co2 > 0 && (
                        <div className="flex items-center gap-1 bg-white/50 rounded px-2 py-1">
                            <Wind className="w-3 h-3 text-gray-500" />
                            {etat.co2} ppm
                        </div>
                    )}
                    {etat.niveau_eau !== null && (
                        <div className="col-span-2 bg-white/50 rounded px-2 py-1">
                            <div className="flex justify-between text-xs mb-1">
                                <span>🛢️ Tank</span>
                                <span className="font-bold">{etat.volume_m3?.toFixed(2)} m³ ({etat.niveau_eau?.toFixed(0)}%)</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full transition-all ${etat.niveau_eau > 60 ? 'bg-blue-500' : etat.niveau_eau > 30 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                    style={{ width: `${etat.niveau_eau}%` }}
                                />
                            </div>
                        </div>
                    )}
                    {etat.temp_compost !== null && (
                        <div className="col-span-2 flex items-center gap-1 bg-white/50 rounded px-2 py-1">
                            <span>🔥</span>
                            Compost: {etat.temp_compost?.toFixed(0)}°C
                            <span className={etat.temp_compost >= 45 ? 'text-green-600' : 'text-yellow-600'}>
                                ({etat.temp_compost >= 45 ? 'Actif' : 'Lent'})
                            </span>
                        </div>
                    )}
                </div>

                {/* Équipements actifs (icônes) */}
                {actifs.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-gray-200">
                        {actifs.map(eq => {
                            const config = TYPES_EQUIPEMENTS[eq.type_equipement] || {};
                            return (
                                <span 
                                    key={eq.id}
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs animate-pulse"
                                    style={{ backgroundColor: config.color }}
                                    title={eq.nom}
                                >
                                    {config.icon}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU PANNEAU CONTRÔLE MODULE
    // ═══════════════════════════════════════════════════════════════════════
    const renderControlPanel = () => {
        if (!selectedModule) {
            return (
                <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-500">
                    <Settings className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Sélectionnez un module pour voir ses équipements</p>
                </div>
            );
        }

        const etat = getEtatModule(selectedModule);
        const moduleEquipements = getEquipementsByModule(selectedModule);

        if (!etat) return null;

        return (
            <div className="bg-white rounded-xl border-2 border-blue-200 shadow-lg overflow-hidden">
                {/* Header */}
                <div className="bg-blue-500 text-white p-4">
                    <h2 className="text-lg font-bold flex items-center gap-2">
                        <span className="text-2xl">{etat.zone_icone}</span>
                        {etat.zone_nom || etat.module_nom}
                    </h2>
                    <p className="text-blue-100 text-sm">{etat.code_module}</p>
                </div>

                {/* Mesures détaillées */}
                <div className="p-4 bg-blue-50 border-b grid grid-cols-4 gap-3 text-center">
                    <div>
                        <div className="text-2xl font-bold text-red-500">{etat.temperature?.toFixed(1) || '--'}°C</div>
                        <div className="text-xs text-gray-500">Température</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-blue-500">{etat.humidity?.toFixed(0) || '--'}%</div>
                        <div className="text-xs text-gray-500">Humidité air</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-amber-600">{etat.humidity_sol?.toFixed(0) || '--'}%</div>
                        <div className="text-xs text-gray-500">Humidité sol</div>
                    </div>
                    <div>
                        <div className="text-2xl font-bold text-gray-600">{etat.co2 || '--'}</div>
                        <div className="text-xs text-gray-500">CO₂ ppm</div>
                    </div>
                </div>

                {/* Liste équipements */}
                <div className="p-4">
                    <h3 className="font-bold text-gray-700 mb-3">Équipements ({moduleEquipements.length})</h3>
                    
                    {moduleEquipements.length === 0 ? (
                        <p className="text-gray-400 text-sm">Aucun équipement sur ce module</p>
                    ) : (
                        <div className="space-y-2">
                            {moduleEquipements.map(eq => {
                                const config = TYPES_EQUIPEMENTS[eq.type_equipement] || {};
                                const isOn = eq.etat === 'on';
                                const isAuto = eq.etat === 'auto';

                                return (
                                    <div 
                                        key={eq.id}
                                        className={`
                                            flex items-center justify-between p-3 rounded-lg border-2 transition-all
                                            ${isOn ? 'border-green-400 bg-green-50' : isAuto ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'}
                                        `}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span 
                                                className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${isOn ? 'animate-pulse' : ''}`}
                                                style={{ backgroundColor: isOn ? config.color : '#d1d5db', color: 'white' }}
                                            >
                                                {config.icon}
                                            </span>
                                            <div>
                                                <div className="font-medium text-gray-800">{eq.nom}</div>
                                                <div className="text-xs text-gray-500">
                                                    {config.label}
                                                    {eq.source_code && <span className="ml-2 text-blue-500">→ lié au {eq.source_nom}</span>}
                                                    {eq.debit_nominal && <span className="ml-2">{eq.debit_nominal} L/min</span>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Boutons contrôle */}
                                        <div className="flex gap-1">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleEquipement(eq, 'on'); }}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isOn ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-green-100'}`}
                                            >
                                                ON
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleEquipement(eq, 'off'); }}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${!isOn && !isAuto ? 'bg-red-500 text-white' : 'bg-gray-200 hover:bg-red-100'}`}
                                            >
                                                OFF
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleEquipement(eq, 'auto'); }}
                                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isAuto ? 'bg-yellow-500 text-white' : 'bg-gray-200 hover:bg-yellow-100'}`}
                                            >
                                                AUTO
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Info chaînage */}
                {moduleEquipements.some(e => e.source_module_id) && (
                    <div className="px-4 pb-4">
                        <div className="bg-blue-100 rounded-lg p-3 text-sm text-blue-700">
                            <Zap className="w-4 h-4 inline mr-1" />
                            <strong>Chaînage automatique :</strong> Les vannes et brumisations activent automatiquement la pompe du tank
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU LIENS EAU ACTIFS
    // ═══════════════════════════════════════════════════════════════════════
    const renderLiensEauActifs = () => {
        if (liensEau.length === 0) return null;

        return (
            <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                <h3 className="font-bold text-blue-700 mb-2 flex items-center gap-2">
                    💧 Irrigations actives ({liensEau.length})
                </h3>
                <div className="space-y-2">
                    {liensEau.map((lien, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2">
                            <span className="font-medium text-amber-600">🛢️ {lien.source_nom}</span>
                            <span className="text-blue-500">→→→</span>
                            <span className="font-medium text-green-600">🌱 {lien.dest_nom}</span>
                            <span className="text-gray-400 text-xs ml-auto">{lien.debit_nominal} L/min</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU LOG ACTIONS
    // ═══════════════════════════════════════════════════════════════════════
    const renderActionLog = () => {
        return (
            <div className="bg-gray-800 rounded-xl p-4 text-white">
                <h3 className="font-bold mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Journal d'actions
                </h3>
                <div className="space-y-1 max-h-40 overflow-y-auto text-xs font-mono">
                    {actionLog.length === 0 ? (
                        <p className="text-gray-500">Aucune action récente</p>
                    ) : (
                        actionLog.map((log, i) => (
                            <div key={i} className="flex gap-2">
                                <span className="text-gray-500">{log.time}</span>
                                <span className={log.message.includes('⚡') ? 'text-yellow-400' : 'text-green-400'}>{log.message}</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU PRINCIPAL
    // ═══════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Chargement supervision...</span>
            </div>
        );
    }

    const nbEquipementsOn = Object.values(equipementsActifs).flat().length;
    const tankEtat = etats.find(e => e.module_id === 16);

    return (
        <div className="p-4 bg-gray-100 min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <Activity className="w-7 h-7 text-blue-600" />
                    Supervision IoT
                </h1>

                <div className="flex items-center gap-3">
                    {/* Status simulation */}
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${simulationStatus.running ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        <span className={`w-3 h-3 rounded-full ${simulationStatus.running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                        {simulationStatus.running ? `Simulation ACTIVE - Tick #${simulationStatus.tickCount}` : 'Simulation ARRÊTÉE'}
                    </div>

                    {/* Boutons contrôle */}
                    <button
                        onClick={toggleSimulation}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium ${simulationStatus.running ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}
                    >
                        {simulationStatus.running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        {simulationStatus.running ? 'Pause' : 'Démarrer'}
                    </button>

                    <button onClick={resetSimulation} className="px-4 py-2 bg-gray-500 text-white rounded-xl">
                        🔄 Reset
                    </button>

                    <button onClick={loadData} className="p-2 bg-gray-200 rounded-xl hover:bg-gray-300">
                        <RefreshCw className="w-5 h-5" />
                    </button>

                    {lastUpdate && (
                        <span className="text-xs text-gray-500">
                            MAJ: {lastUpdate.toLocaleTimeString('fr-FR')}
                        </span>
                    )}
                </div>
            </div>

            {/* Stats globales */}
            <div className="grid grid-cols-5 gap-4 mb-4">
                <div className="bg-white rounded-xl p-4 border-2 border-green-200 text-center">
                    <div className="text-3xl font-bold text-green-600">{etats.length}</div>
                    <div className="text-sm text-gray-500">Modules</div>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-blue-200 text-center">
                    <div className="text-3xl font-bold text-blue-600">{equipements.length}</div>
                    <div className="text-sm text-gray-500">Équipements</div>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-amber-200 text-center">
                    <div className={`text-3xl font-bold ${nbEquipementsOn > 0 ? 'text-amber-600 animate-pulse' : 'text-gray-400'}`}>{nbEquipementsOn}</div>
                    <div className="text-sm text-gray-500">Actifs (ON)</div>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-cyan-200 text-center">
                    <div className="text-3xl font-bold text-cyan-600">{liensEau.length}</div>
                    <div className="text-sm text-gray-500">Irrigations</div>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-purple-200 text-center">
                    <div className={`text-3xl font-bold ${tankEtat && tankEtat.niveau_eau < 30 ? 'text-red-600' : 'text-purple-600'}`}>
                        {tankEtat?.niveau_eau?.toFixed(0) || '--'}%
                    </div>
                    <div className="text-sm text-gray-500">Tank</div>
                </div>
            </div>

            {/* Layout principal */}
            <div className="grid grid-cols-3 gap-4">
                {/* Colonne gauche : Modules */}
                <div className="col-span-2 space-y-4">
                    {/* Liens eau actifs */}
                    {renderLiensEauActifs()}

                    {/* Grille modules */}
                    <div className="grid grid-cols-3 gap-3">
                        {etats.map(etat => renderModuleCard(etat))}
                    </div>
                </div>

                {/* Colonne droite : Contrôles + Log */}
                <div className="space-y-4">
                    {renderControlPanel()}
                    {renderActionLog()}
                </div>
            </div>
        </div>
    );
};

export default SupervisionIoT;